if (!customElements.get('sc-product-shipping-notice')) {
  class ScProductShippingNotice extends HTMLElement {
    constructor() {
      super();

      this.rules = [];
      this.texts = {};
      this.variants = new Map();
      this.variantChangeUnsubscriber = null;
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onExternalUpdate = this.onExternalUpdate.bind(this);
    }

    connectedCallback() {
      this.rules = this.parseJson('[data-shipping-notice-rules]', []);
      this.texts = this.parseJson('[data-shipping-notice-texts]', {});
      this.variants = new Map(
        this.parseJson('[data-shipping-notice-variants]', []).map((variant) => [String(variant.id), variant])
      );

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
      }

      document.addEventListener('sc:shipping-notice:update', this.onExternalUpdate);

      this.update({
        weight: this.dataset.weight,
        price: this.dataset.price,
        requiresShipping: this.dataset.requiresShipping !== 'false',
      });
    }

    disconnectedCallback() {
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
        this.variantChangeUnsubscriber = null;
      }

      document.removeEventListener('sc:shipping-notice:update', this.onExternalUpdate);
    }

    onVariantChange(event) {
      const variant = event?.data?.variant;

      if (variant) this.updateWithVariant(variant);
    }

    onExternalUpdate(event) {
      const detail = event?.detail || {};

      if (detail.variant) {
        this.updateWithVariant(detail.variant, detail);
        return;
      }

      this.update(detail);
    }

    updateWithVariant(variant, overrides = {}) {
      if (!variant) return;

      const variantData = this.getVariant(variant) || variant;

      this.update({
        weight: variantData.weight ?? variantData.grams ?? variant.weight ?? variant.grams,
        price: overrides.price ?? variantData.price ?? variant.price,
        requiresShipping:
          overrides.requiresShipping ??
          variantData.requiresShipping ??
          variantData.requires_shipping ??
          variant.requiresShipping ??
          variant.requires_shipping ??
          true,
      });
    }

    getVariant(variant) {
      const variantId = typeof variant === 'object' ? variant.id : variant;

      if (!variantId) return null;

      return this.variants.get(String(variantId)) || null;
    }

    update({ weight, price, requiresShipping = true } = {}) {
      const normalizedWeight = ScProductShippingNotice.toNumber(weight);
      const normalizedPrice = ScProductShippingNotice.toNumber(price);
      const normalizedRequiresShipping = requiresShipping !== false && requiresShipping !== 'false';
      const bestRule =
        normalizedRequiresShipping && normalizedWeight > 0 ? this.findBestRule(normalizedWeight, normalizedPrice) : null;

      if (!bestRule) {
        this.render('');
        return;
      }

      if (bestRule.price === 0) {
        this.render(this.texts.freeShipping || '');
        return;
      }

      const shippingCostText = (this.texts.shippingCost || '').replace(
        '{{ amount }}',
        this.formatMoney(bestRule.price)
      );

      if (!bestRule.freeShippingThreshold) {
        this.render(shippingCostText);
        return;
      }

      const freeShippingText = (this.texts.freeShippingFrom || '').replace(
        '{{ amount }}',
        this.formatMoney(bestRule.freeShippingThreshold)
      );

      this.render(`${shippingCostText} · ${freeShippingText}`);
    }

    findBestRule(weight, price) {
      let bestRule = null;

      this.rules.forEach((rule) => {
        const minWeight = ScProductShippingNotice.toNumber(rule.minWeight);
        const maxWeight = ScProductShippingNotice.toNumber(rule.maxWeight, Number.MAX_SAFE_INTEGER);

        if (weight < minWeight || weight > maxWeight) return;

        const shippingPrice = rule.price === null || rule.price === undefined ? 0 : ScProductShippingNotice.toNumber(rule.price);
        const freeShippingThreshold = ScProductShippingNotice.toNumber(rule.freeShippingThreshold);
        const effectivePrice = freeShippingThreshold > 0 && price >= freeShippingThreshold ? 0 : shippingPrice;
        const normalizedRule = {
          price: effectivePrice,
          freeShippingThreshold,
        };

        if (ScProductShippingNotice.isBetterRule(normalizedRule, bestRule)) bestRule = normalizedRule;
      });

      return bestRule;
    }

    render(text) {
      const textElement = this.querySelector('[data-shipping-notice-text]');

      if (textElement) textElement.textContent = text;

      this.hidden = !text;
    }

    parseJson(selector, fallback) {
      const script = this.querySelector(selector);

      if (!script) return fallback;

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return fallback;
      }
    }

    formatMoney(amount) {
      const moneyFormat = this.texts.moneyFormat || '€{{amount_with_comma_separator}}';

      return ScProductShippingNotice.formatMoney(amount, moneyFormat)
        .replace(/<[^>]*>/g, '')
        .replace(/([,.]00)(?!\d)/, '');
    }

    static isBetterRule(rule, currentRule) {
      if (!currentRule) return true;
      if (rule.price < currentRule.price) return true;
      if (rule.price > currentRule.price) return false;
      if (rule.price === 0) return false;
      if (!currentRule.freeShippingThreshold && rule.freeShippingThreshold) return true;

      return Boolean(
        rule.freeShippingThreshold &&
          currentRule.freeShippingThreshold &&
          rule.freeShippingThreshold < currentRule.freeShippingThreshold
      );
    }

    static toNumber(value, fallback = 0) {
      const number = Number(value);

      return Number.isFinite(number) ? number : fallback;
    }

    static formatMoney(cents, format) {
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const placeholder = format.match(placeholderRegex)?.[1] || 'amount';
      const value = ScProductShippingNotice.toNumber(cents) / 100;
      let formattedValue;

      switch (placeholder) {
        case 'amount_no_decimals':
          formattedValue = ScProductShippingNotice.formatNumber(value, 0);
          break;
        case 'amount_with_comma_separator':
          formattedValue = ScProductShippingNotice.formatNumber(value, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          formattedValue = ScProductShippingNotice.formatNumber(value, 0, '.', ',');
          break;
        default:
          formattedValue = ScProductShippingNotice.formatNumber(value, 2);
      }

      return format.replace(placeholderRegex, formattedValue);
    }

    static formatNumber(value, decimals = 2, thousands = ',', decimal = '.') {
      const parts = value.toFixed(decimals).split('.');

      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

      return parts.join(decimal);
    }
  }

  customElements.define('sc-product-shipping-notice', ScProductShippingNotice);
}
